import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Slow video component with key for refresh
const SlowVideo = ({ src, className, testId }) => {
  const videoRef = useRef(null);
  
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = 0.6;
      videoRef.current.load();
    }
  }, [src]);
  
  return (
    <video
      ref={videoRef}
      key={src}
      autoPlay
      muted
      loop
      playsInline
      className={className}
      data-testid={testId}
    >
      <source src={src} type="video/mp4" />
    </video>
  );
};

const GallerySection = () => {
  const { t, getCustomMedia } = useLanguage();
  const [gallery, setGallery] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [currentDetailIndex, setCurrentDetailIndex] = useState(0);
  
  // Lock body scroll when modal is open
  useEffect(() => {
    if (showDetailsModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showDetailsModal]);
  
  // Featured video URL - use custom or default
  const defaultVideo = "https://customer-assets.emergentagent.com/job_mystic-sauna/artifacts/d9xexnys_0204-01.mp4";
  const featuredVideo = getCustomMedia('gallery', 'video') || defaultVideo;
  
  // Detail images for the popup - Sauna images first
  const detailImages = [
    {
      id: 'd1',
      url: 'https://customer-assets.emergentagent.com/job_mystic-sauna/artifacts/vtj30mrb_kyu_sauna_inside.jpg',
      title: 'Interior Atmosphere',
    },
    {
      id: 'd2',
      url: 'https://customer-assets.emergentagent.com/job_mystic-sauna/artifacts/xxx1f9vu_IMG_0918%2B.jpg',
      title: 'The Ritual Space',
    },
    {
      id: 'd3',
      url: 'https://customer-assets.emergentagent.com/job_mystic-sauna/artifacts/ea1151if_IMG_0953.jpg',
      title: 'Craftsmanship',
    },
    {
      id: 'd4',
      url: 'https://customer-assets.emergentagent.com/job_mystic-sauna/artifacts/yxj4fpre_Kyu_offroad_sauna_AXO_02.png',
      title: 'Axonometric View',
    },
    {
      id: 'd5',
      url: 'https://customer-assets.emergentagent.com/job_mystic-sauna/artifacts/pwuxn29o_Bildschirmfoto%202022-12-22%20um%2021.50.25.png',
      title: 'The Sanctuary Blueprint',
    },
    {
      id: 'd6',
      url: 'https://customer-assets.emergentagent.com/job_mystic-sauna/artifacts/tcas5yjv_Bildschirmfoto%202022-12-22%20um%2021.49.35.png',
      title: 'Technical Drawing',
    },
    {
      id: 'd7',
      url: 'https://customer-assets.emergentagent.com/job_mystic-sauna/artifacts/8g91dei6_Bildschirmfoto%202023-10-06%20um%2013.33.00.png',
      title: 'Architectural Vision',
    },
    {
      id: 'd8',
      url: 'https://customer-assets.emergentagent.com/job_mystic-sauna/artifacts/kdfo0c2j_Bildschirmfoto%202024-11-18%20um%2014.08.38.png',
      title: 'Design Concept',
    },
    {
      id: 'd9',
      url: 'https://customer-assets.emergentagent.com/job_mystic-sauna/artifacts/5kdgeegd_Bildschirmfoto%202025-02-13%20um%2021.53.37.png',
      title: 'The Experience',
    },
  ];
  
  // Default gallery items (can be replaced via admin)
  const defaultGallery = [
    {
      id: '1',
      type: 'image',
      url: 'https://images.unsplash.com/photo-1540555700478-4be289fbec6d?w=800&q=80',
      title: 'The Aufguss Ritual',
    },
    {
      id: '2',
      type: 'image',
      url: 'https://images.unsplash.com/photo-1507652313519-d4e9174996dd?w=800&q=80',
      title: 'Sauna Chamber',
    },
    {
      id: '3',
      type: 'image',
      url: 'https://images.unsplash.com/photo-1433086966358-54859d0ed716?w=800&q=80',
      title: 'Alpine Plunge',
    },
    {
      id: '4',
      type: 'image',
      url: 'https://images.unsplash.com/photo-1448375240586-882707db888b?w=800&q=80',
      title: 'Forest Sanctuary',
    },
    {
      id: '5',
      type: 'image',
      url: 'https://images.unsplash.com/photo-1518241353330-0f7941c2d9b5?w=800&q=80',
      title: 'The Ritual',
    },
    {
      id: '6',
      type: 'image',
      url: 'https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?w=800&q=80',
      title: 'Dawn Awakening',
    },
  ];
  
  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const response = await axios.get(`${API}/gallery`);
        if (response.data.length > 0) {
          setGallery(response.data);
        } else {
          setGallery(defaultGallery);
        }
      } catch (error) {
        setGallery(defaultGallery);
      }
    };
    fetchGallery();
  }, []);
  
  const nextDetail = () => {
    setCurrentDetailIndex((prev) => (prev + 1) % detailImages.length);
  };
  
  const prevDetail = () => {
    setCurrentDetailIndex((prev) => (prev - 1 + detailImages.length) % detailImages.length);
  };
  
  return (
    <section 
      id="gallery"
      className="relative min-h-screen bg-[#030303] py-32 md:py-48 overflow-hidden"
      data-testid="gallery-section"
    >
      {/* Background Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#2bfecd] opacity-[0.02] blur-[150px] rounded-full" />
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        {/* Header */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1.2 }}
        >
          <motion.p 
            className="text-xs tracking-[0.4em] uppercase text-[#2bfecd]/60 mb-6"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.2 }}
          >
            {t('gallery.subtitle')}
          </motion.p>
          
          <motion.h2 
            className="font-cinzel text-4xl md:text-6xl tracking-[0.15em] uppercase text-white mb-4"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, delay: 0.3 }}
          >
            {t('gallery.title')}
          </motion.h2>
          
          <motion.p
            className="text-xs md:text-sm tracking-[0.3em] uppercase text-[#2bfecd]/70 mb-2"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.4 }}
          >
            Munich's First Mobile Sauna
          </motion.p>
          
          <motion.p
            className="text-xs tracking-[0.2em] uppercase text-white/40 mb-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.5 }}
          >
            at Reitschule Munich
          </motion.p>
          
          <motion.div 
            className="flex items-center justify-center gap-4"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.5 }}
          >
            <div className="w-16 h-px bg-gradient-to-r from-transparent to-[#2bfecd]/30" />
            <div className="w-2 h-2 rotate-45 border border-[#2bfecd]/30" />
            <div className="w-16 h-px bg-gradient-to-l from-transparent to-[#2bfecd]/30" />
          </motion.div>
        </motion.div>
        
        {/* Featured Video with 50% Dimming */}
        <motion.div 
          className="relative mb-8 overflow-hidden"
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1.5, delay: 0.3 }}
        >
          <div className="relative aspect-video max-w-5xl mx-auto">
            {/* Video */}
            <SlowVideo
              src={featuredVideo}
              className="w-full h-full object-cover"
              testId="featured-video"
            />
            
            {/* Subtiler Dimming Layer für einheitlichen Look */}
            <div className="absolute inset-0 bg-black/10 pointer-events-none" />
            
            {/* Sanfte Verläufe an den Rändern für smooth integration */}
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute top-0 left-0 right-0 h-12 bg-gradient-to-b from-[#030303] to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-[#030303] to-transparent" />
              <div className="absolute top-0 bottom-0 left-0 w-12 bg-gradient-to-r from-[#030303] to-transparent" />
              <div className="absolute top-0 bottom-0 right-0 w-12 bg-gradient-to-l from-[#030303] to-transparent" />
            </div>
            
            {/* Subtle border glow */}
            <div className="absolute inset-0 border border-[#2bfecd]/10 pointer-events-none" />
          </div>
        </motion.div>
        
        {/* More Details Button */}
        <motion.div 
          className="flex justify-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1, delay: 0.5 }}
        >
          <button
            onClick={() => setShowDetailsModal(true)}
            className="group relative px-10 py-4 border border-[#2bfecd]/30 text-[#2bfecd] text-xs tracking-[0.3em] uppercase font-cinzel overflow-hidden transition-all duration-700 hover:border-[#2bfecd] hover:shadow-[0_0_40px_rgba(43,254,205,0.25)]"
            data-testid="more-details-button"
          >
            <span className="relative z-10">More Details</span>
            <div className="absolute inset-0 bg-[#2bfecd] transform -translate-x-full group-hover:translate-x-0 transition-transform duration-700" />
            <span className="absolute inset-0 flex items-center justify-center text-black opacity-0 group-hover:opacity-100 transition-opacity duration-700 delay-100 z-20 tracking-[0.3em]">
              More Details
            </span>
          </button>
        </motion.div>
        
        {/* KPIs Section */}
        <motion.div 
          className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto mb-16"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1, delay: 0.6 }}
        >
          {[
            { number: '90°', label: 'Sauna' },
            { number: '3°', label: 'Ice Bath' },
            { number: '7 Days', label: 'Open' },
            { number: '∞', label: 'Endless Sessions' },
          ].map((stat, index) => (
            <motion.div 
              key={stat.label}
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.7 + index * 0.1 }}
            >
              <p className="font-cinzel text-3xl md:text-4xl text-[#2bfecd] mb-2">{stat.number}</p>
              <p className="text-[10px] tracking-[0.2em] uppercase text-white/40">{stat.label}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
      
      {/* Details Modal - Full Gallery Popup */}
      <AnimatePresence>
        {showDetailsModal && (
          <motion.div 
            className="fixed inset-0 z-50 flex items-center justify-center bg-black overflow-hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            onClick={(e) => e.target === e.currentTarget && setShowDetailsModal(false)}
          >
            {/* Close Button */}
            <motion.button 
              onClick={() => setShowDetailsModal(false)}
              className="absolute top-8 right-8 text-white/30 hover:text-[#2bfecd] transition-colors duration-300 z-50"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              whileHover={{ scale: 1.1 }}
              data-testid="close-details-modal"
            >
              <X className="w-8 h-8" strokeWidth={1} />
            </motion.button>
            
            {/* Modal Content */}
            <div className="w-full max-w-6xl mx-auto px-6 max-h-[90vh] overflow-y-auto">
              {/* Title */}
              <motion.div 
                className="text-center mb-12"
                initial={{ opacity: 0, y: -30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.8 }}
              >
                <p className="text-xs tracking-[0.4em] uppercase text-[#2bfecd]/60 mb-4">
                  Exclusive Preview
                </p>
                <h3 className="font-cinzel text-3xl md:text-5xl tracking-[0.15em] uppercase text-white">
                  The Vision
                </h3>
                <div className="flex items-center justify-center gap-4 mt-6">
                  <div className="w-12 h-px bg-gradient-to-r from-transparent to-[#2bfecd]/30" />
                  <div className="w-2 h-2 rotate-45 border border-[#2bfecd]/30" />
                  <div className="w-12 h-px bg-gradient-to-l from-transparent to-[#2bfecd]/30" />
                </div>
              </motion.div>
              
              {/* Main Image Display */}
              <motion.div 
                className="relative"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3, duration: 0.8 }}
              >
                <div className="relative aspect-video max-w-4xl mx-auto overflow-hidden">
                  {/* Current Image */}
                  <AnimatePresence mode="wait">
                    <motion.img
                      key={currentDetailIndex}
                      src={detailImages[currentDetailIndex].url}
                      alt={detailImages[currentDetailIndex].title}
                      className="w-full h-full object-contain"
                      initial={{ opacity: 0, x: 50 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -50 }}
                      transition={{ duration: 0.5 }}
                    />
                  </AnimatePresence>
                  
                  {/* Border */}
                  <div className="absolute inset-0 border border-[#2bfecd]/10" />
                </div>
                
                {/* Navigation Arrows */}
                <button 
                  onClick={prevDetail}
                  className="absolute left-4 top-1/2 -translate-y-1/2 p-3 border border-white/10 text-white/40 hover:text-[#2bfecd] hover:border-[#2bfecd]/30 transition-all duration-300"
                  data-testid="prev-detail"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button 
                  onClick={nextDetail}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-3 border border-white/10 text-white/40 hover:text-[#2bfecd] hover:border-[#2bfecd]/30 transition-all duration-300"
                  data-testid="next-detail"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              </motion.div>
              
              {/* Image Title */}
              <motion.p 
                className="text-center mt-8 text-sm tracking-[0.25em] uppercase text-white/50"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
              >
                {detailImages[currentDetailIndex].title}
              </motion.p>
              
              {/* Thumbnail Navigation */}
              <motion.div 
                className="flex justify-center gap-4 mt-10"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
              >
                {detailImages.map((img, index) => (
                  <button
                    key={img.id}
                    onClick={() => setCurrentDetailIndex(index)}
                    className={`relative w-20 h-14 overflow-hidden border transition-all duration-300 ${
                      index === currentDetailIndex 
                        ? 'border-[#2bfecd] shadow-[0_0_20px_rgba(43,254,205,0.3)]' 
                        : 'border-white/10 hover:border-white/30'
                    }`}
                    data-testid={`detail-thumb-${index}`}
                  >
                    <img 
                      src={img.url} 
                      alt={img.title}
                      className="w-full h-full object-cover"
                    />
                    {index !== currentDetailIndex && (
                      <div className="absolute inset-0 bg-black/50" />
                    )}
                  </button>
                ))}
              </motion.div>
              
              {/* Counter */}
              <motion.p 
                className="text-center mt-6 text-[10px] tracking-[0.3em] uppercase text-white/20"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7 }}
              >
                {currentDetailIndex + 1} / {detailImages.length}
              </motion.p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Single Item Lightbox Modal */}
      {selectedItem && (
        <motion.div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/95 backdrop-blur-xl"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setSelectedItem(null)}
        >
          <motion.div 
            className="relative max-w-5xl max-h-[80vh] w-full"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.4 }}
            onClick={(e) => e.stopPropagation()}
          >
            {selectedItem.type === 'video' ? (
              <video
                src={selectedItem.url.startsWith('/') ? `${BACKEND_URL}${selectedItem.url}` : selectedItem.url}
                className="w-full h-full object-contain"
                controls
                autoPlay
              />
            ) : (
              <img
                src={selectedItem.url.startsWith('/') ? `${BACKEND_URL}${selectedItem.url}` : selectedItem.url}
                alt={selectedItem.title}
                className="w-full h-full object-contain"
              />
            )}
            <p className="text-center mt-6 text-sm tracking-[0.2em] uppercase text-white/60">
              {selectedItem.title}
            </p>
          </motion.div>
        </motion.div>
      )}
    </section>
  );
};

export default GallerySection;
